import { UploadedFile } from 'components/Dropzone';
import { useAiAgent } from 'contexts/AiAgentContext';
import { useLayoutContext } from 'contexts/LayoutContext';
import { UploadType } from 'further-types/files';
import useFileUpload from 'hooks/ui/useFileUpload';
import api from 'lib/trpcClient';
import { useEffect, useRef, useState } from 'react';
import { useChatHydration } from './useChatHydration';
import { useFileStaging } from './useFileStaging';
import { useScrollAnchor } from './useScrollAnchor';
import { ChatMessage, PendingInvoice } from '../types';
import { fileToBase64, getMediaType } from '../utils';
import {
  ChatMessageRole,
  ResponseSegmentType,
} from 'further-types/lp/ai-agent';
import { useChatSessionManager } from './useChatSessionManager';
import { CalendarTime } from 'further-ui/utils';

export const useChatMessages = () => {
  const { pendingNotification, clearNotification } = useAiAgent();
  const { selectedVehicleId, breadcrumbs } = useLayoutContext();
  const { uploadFile } = useFileUpload();
  const chatMutation = api.aiAgent.chat.useMutation();
  const { activeSessionId, getSessionIdForSend } = useChatSessionManager();

  const [messages, setMessages] = useState<Array<ChatMessage>>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { messagesEndRef, scrollToBottom, snapToBottom } = useScrollAnchor();
  const inputRef = useRef<HTMLInputElement>(null);
  const refocusAfterLoadRef = useRef(false);
  const pendingInvoiceRef = useRef<PendingInvoice | null>(null);

  const {
    stagedFile,
    setStagedFile,
    stageFile,
    handleFileInputChange,
    fileInputRef,
  } = useFileStaging({
    isLoading,
    onError: (msg) => addMessageToThread(undefined, msg),
  });

  useChatHydration({
    activeSessionId,
    selectedVehicleId,
    setMessages,
    onHydrated: snapToBottom,
  });

  useEffect(() => {
    if (!isLoading && refocusAfterLoadRef.current) {
      refocusAfterLoadRef.current = false;
      inputRef.current?.focus();
    }
  }, [isLoading]);

  useEffect(() => {
    if (pendingNotification) {
      addMessageToThread({
        role: ChatMessageRole.Action,
        content: pendingNotification,
      });
      clearNotification();
    }
  }, [pendingNotification, clearNotification]);

  const addMessageToThread = (message?: ChatMessage, agentError?: string) => {
    if (agentError) {
      const assistantErrorMessage: ChatMessage = {
        role: ChatMessageRole.Assistant,
        segments: [{ type: ResponseSegmentType.Text, content: agentError }],
        pendingInvoice: null,
        timestamp: new CalendarTime(),
      };
      setMessages((prev) => [...prev, assistantErrorMessage]);
    } else if (message) {
      setMessages((prev) => [...prev, message]);
    }
    scrollToBottom();
  };

  const sendMessage = async (text: string, fileOverride?: File) => {
    const currentStagedFile = fileOverride ?? stagedFile;
    if (!text.trim() && !currentStagedFile) return;
    if (!selectedVehicleId || isLoading) return;

    setIsLoading(true);

    if (currentStagedFile) {
      try {
        const mediaType = getMediaType(currentStagedFile);
        if (!mediaType) {
          addMessageToThread(
            undefined,
            'Unsupported file type. Please upload a PDF.',
          );
          setIsLoading(false);
          return;
        }
        const uploadedFile = Object.assign(currentStagedFile, {
          path: currentStagedFile.name,
        }) as UploadedFile;

        const [base64, uploadResult] = await Promise.all([
          fileToBase64(currentStagedFile),
          uploadFile(uploadedFile, UploadType.LpTransactionAttachments),
        ]);

        if (!uploadResult.data) {
          addMessageToThread(
            undefined,
            uploadResult.error?.message ?? 'Failed to upload the file.',
          );
          setIsLoading(false);
          return;
        }

        pendingInvoiceRef.current = {
          base64,
          mediaType,
          fileName: currentStagedFile.name,
          uploadedPath: uploadResult.data.filePath,
          uploadedName: uploadResult.data.name ?? currentStagedFile.name,
          uploadedPreview: uploadResult.data.url,
        };
      } catch {
        addMessageToThread(
          undefined,
          'Failed to process the file. Please try again.',
        );
        setIsLoading(false);
        return;
      }
    }

    const messageContent =
      text.trim() || (currentStagedFile ? 'Please process this file' : '');

    const userMessage: ChatMessage = {
      role: ChatMessageRole.User,
      content: messageContent,
      fileName: currentStagedFile
        ? pendingInvoiceRef.current?.fileName
        : undefined,
      timestamp: new CalendarTime(),
    };
    addMessageToThread(userMessage);
    setInputText('');
    setStagedFile(null);
    refocusAfterLoadRef.current = true;

    try {
      const currentPage =
        breadcrumbs
          .map((b) => (typeof b === 'string' ? b : b.label))
          .join(' / ') || undefined;

      const response = await chatMutation.mutateAsync({
        vehicleId: selectedVehicleId,
        message: { role: ChatMessageRole.User, content: messageContent },
        file:
          currentStagedFile && pendingInvoiceRef.current
            ? {
                base64: pendingInvoiceRef.current.base64,
                mediaType: pendingInvoiceRef.current.mediaType,
                fileName: pendingInvoiceRef.current.fileName,
              }
            : undefined,
        sessionId: getSessionIdForSend(),
        currentPage,
        userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });

      // this should really use the isIncomeExpenseNav from the AgentResponse component, or be cleared by the component itself
      const includePendingInvoiceInReply =
        pendingInvoiceRef.current &&
        response.segments.some(
          (s) => s.type === ResponseSegmentType.Navigation,
        );

      const replyMessage: ChatMessage = {
        role: ChatMessageRole.Assistant,
        segments: response.segments,
        pendingInvoice: includePendingInvoiceInReply
          ? pendingInvoiceRef.current
          : null,
        timestamp: new CalendarTime(),
      };
      addMessageToThread(replyMessage);

      if (includePendingInvoiceInReply) {
        pendingInvoiceRef.current = null;
      }
    } catch (_) {
      pendingInvoiceRef.current = null;
      addMessageToThread(
        undefined,
        'Sorry, something went wrong. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  return {
    messages,
    inputText,
    setInputText,
    isLoading,
    stagedFile,
    setStagedFile,
    sendMessage,
    stageFile,
    handleFileInputChange,
    messagesEndRef,
    inputRef,
    fileInputRef,
    snapToBottom,
  };
};
